import React, { useEffect, useMemo, useState } from 'react';
import { Form } from 'antd';
import { ReactComponent as Sms } from '../../../../resources/images/svg/sms.svg';
import { ReactComponent as Refresh } from '../../../../resources/images/svg/refresh.svg';
import { SendVerification, SubmitVerification } from '../../../../services/customer/verificationServices';
import CustomerNumberInput from '../customerNumberInput/CustomerNumberInput';
import CustomerTextInput from '../customerTextInput/CustomerTextInput';
import uid from '../../../../utils/functions/uid';
import useTranslation from '../../../hooks/translation/useTranslation';

const buttonClass = 'basis-1/2 rounded-lg border-0 py-2 px-6 text-base flex items-center justify-center gap-2 ease-out duration-300 cursor-pointer';

function CustomerVerificationInput({
  className, errors, form, setErrors, verifyType,
}) {
  const { trans } = useTranslation();
  const codeInputName = useMemo(() => `code_${uid()}`, []);
  const verificationCodeName = useMemo(() => `verification_code_${uid()}`, []);
  const [verificationStarted, setVerificationStarted] = useState(false);
  const [verificationTime, setVerificationTime] = useState(0);
  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(false);
  const phoneCode = Form.useWatch('country_phone_code', form);
  const phone = Form.useWatch('phone_number', form);
  const email = Form.useWatch('email', form);
  const verification = verifyType || Form.useWatch('verification', form);

  useEffect(() => {
    if (verificationStarted && verificationTime > 0) {
      const timer = setTimeout(() => {
        // Your code here to be executed after 1 second
        setVerificationTime((prev) => prev - 1);
      }, 1000);

      return () => {
        // Clean up the timer if the component unmounts before the timer finishes
        clearTimeout(timer);
      };
    }
    return undefined; // Explicitly return undefined when verificationStarted is false
  }, [verificationStarted, verificationTime]);

  const sendVerification = () => {
    setLoading(true);
    SendVerification({
      phone_number: phone?.replace(/\s/g, ''),
      country_phone_code: phoneCode,
      email,
      verification: verification === 1 ? 'email' : 'sms',
    }).then((res) => {
      setLoading(false);
      if (res.status === 'success') {
        setVerificationStarted(true);
        setVerificationTime(30);
      }
    }).catch((e) => {
      setLoading(false);
      const codeErrors = e.response?.data?.errors;
      if (codeErrors?.code) {
        codeErrors[codeInputName] = codeErrors?.code;
      }
      if (codeErrors?.verification_code) {
        codeErrors[verificationCodeName] = codeErrors?.verification_code;
      }
      setErrors(codeErrors);
    });
  };

  const submitVerification = () => {
    setErrors(null);
    setLoading(true);
    SubmitVerification({
      phone_number: phone?.replace(/\s/g, ''),
      country_phone_code: phoneCode,
      email,
      verification_code: form.getFieldValue(codeInputName),
      verification: verification === 1 ? 'email' : 'sms',
    }).then((res) => {
      setLoading(false);
      if (res.status === 'success') {
        setVerified(true);
        if (!verifyType) {
          form.setFieldValue('verified', verification === 1 ? 'email' : 'phone_number');
        } else {
          form.setFieldValue(`verified_${verification === 1 ? 'email' : 'phone'}`, true);
        }
        setVerificationStarted(false);
        setVerificationTime(0);
      }
    }).catch((e) => {
      setLoading(false);
      const codeErrors = e.response?.data?.errors;
      if (codeErrors?.code) {
        codeErrors[codeInputName] = codeErrors?.code;
      }
      if (codeErrors?.verification_code) {
        codeErrors[verificationCodeName] = codeErrors?.verification_code;
      }
      setErrors(codeErrors);
    });
  };

  useEffect(() => {
    if (verifyType === 'sms' || verification === 2) {
      setVerified(false);
      form.resetFields(['verified_phone', 'verified']);
    }
  }, [phone, phoneCode]);

  useEffect(() => {
    if (verifyType === 'email' || verification === 1) {
      setVerified(false);
      form.resetFields(['verified_email', 'verified']);
    }
  }, [email]);

  useEffect(() => {
    setVerified(false);
    form.resetFields(['verified_email', 'verified', 'verified_phone']);
  }, [verification]);

  return (
    <div className={className}>
      <CustomerTextInput
        name={verifyType ? `verified_${verification === 1 ? 'email' : 'phone'}` : 'verified'}
        className="hidden"
      />
      {verificationStarted
        ? (
          <div className="flex gap-5 max-w-full">
            <CustomerNumberInput
              name={codeInputName}
              label={trans('sms_code')}
              className="grow"
              errorMessage={errors?.[verificationCodeName]
                || errors?.[codeInputName]
                || errors?.verified
                || errors?.verified_email
                || errors?.verified_phone}
              preserve
            />
            <div className="flex flex-col gap-2">
              <button
                className={`${buttonClass} bg-[#525252] text-white mt-[36.14px] max-h-[60px] h-[60px] cursor-pointer`}
                type="button"
                onClick={submitVerification}
              >
                {trans('confirm')}
              </button>
              <div className="flex items-center ">
                <button className={`flex items-center gap-1 cursor-pointer border-0 bg-transparent ${(verificationTime > 0 || loading) ? 'opacity-50 cursor-not-allowed pointer-events-none' : 'opacity-100 cursor-pointer'}`} type="button" onClick={sendVerification}>
                  <Refresh className={`${loading && 'animate-spin'}`} />
                  <p className="text-xs m-0 whitespace-nowrap text-gray-ch underline">
                    {
                    trans('resend_code')
                  }
                  </p>
                </button>
                <div className="w-[37px] whitespace-nowrap text-xs">
                  {`00:${verificationTime > 9 ? verificationTime : `0${verificationTime}`}`}
                </div>
              </div>
            </div>
          </div>
        )
        : (
          <Form.Item
            className="flex flex-col gap-2"
            name=""
            help={errors?.[codeInputName]
              || errors?.verified
              || errors?.verified_email
              || errors?.verified_phone}
            validateStatus={(errors?.[codeInputName] || errors?.verified || errors?.verified_email || errors?.verified_phone) && 'error'}
          >
            <button
              className={`${buttonClass} bg-[#525252] ${!verified && 'hover:bg-[#212121]'} text-white mt-[36.14px] h-[60px] w-full cursor-pointer max-w-full`}
              type="button"
              onClick={sendVerification}
              disabled={verified}
            >
              {
              loading
                ? <Refresh className="animate-spin fill-white" />
                : <Sms />
            }
              {verified ? trans('code_sent') : trans('send_code')}
            </button>
          </Form.Item>
        )}
    </div>
  );
}

export default CustomerVerificationInput;
